import {shallowRef} from "vue"
import {t} from "@/i18n"
import Idea from "@/types/Idea"
import {FieldRefusal, fieldRefusals, saveErrorMessage} from "@/utils/saveErrors"

/**
 * O que a janela de recusa mostra: as falhas de campo, uma embaixo da outra como no formulário (o nome
 * do campo e a frase), ou — na recusa que não é de campo — a frase.
 *
 * `edit`, quando há: abre a edição do item recusado, e a janela oferece "Editar" — ver o `refuse`.
 */
export type Refusal = { message: string, fields: FieldRefusal[], edit: (() => void) | null }

/**
 * A recusa de uma gravação, numa janela — "Alteração recusada" —, e não na barra de aviso (pedido do
 * usuário): o Salvar do item e do Design, o arrasto do Kanban, a situação, adicionar e desvincular. Na
 * barra, longe de onde se agiu e sumindo sozinha, a frase passava despercebida.
 *
 * Quem DESENHA é uma janela só, no `App.vue`. Estado de módulo, como o `creationRequest`: quem grava
 * mora em todo canto, às vezes três janelas abaixo do casco, e por evento seria um repasse por andar.
 *
 * A falha de CAMPO, no formulário, não vem para cá: continua embaixo do campo (ver o `refuse` do
 * `InstanceInput`). E o que não é gravação — a carga, a exclusão, o "salvo" — continua na barra.
 */
export const refusal = shallowRef<Refusal | null>(null)

/** Mostra uma frase já pronta: para quem tem tradução própria (o domínio, o fluxo citado em fórmula). */
export function showRefusal(message: string) {
    refusal.value = {message, fields: [], edit: null}
}

type RefuseOptions = {
    /** A frase para o que o `saveErrorMessage` não sabe explicar. Por padrão, "Erro inesperado". */
    fallback?: string
    /**
     * Como abrir a edição do item recusado, para quem grava SEM a edição aberta — o arrasto do Kanban, a
     * situação na ficha. Com ela, a janela oferece "Editar" (pedido do usuário), mas só quando a recusa
     * é de um campo DESTE item: a do item do outro lado de um relacionamento editar este não resolve.
     */
    edit?: () => void
}

/**
 * A recusa de uma gravação: as falhas de campo, quando é delas (`fieldRefusals`); senão a frase do
 * `saveErrorMessage`, que é quem traduz, ou o `fallback`.
 *
 * Loga o erro: daqui ele não passa mais pelo `handleError`, que era quem o deixava no console.
 */
export function refuse(err: any, idea?: Idea, {fallback = t('error.unexpected'), edit}: RefuseOptions = {}) {
    console.error(err)
    const fields = fieldRefusals(err, idea)
    refusal.value = {
        message: fields.length ? '' : saveErrorMessage(err, idea) || fallback,
        fields,
        edit: edit && fields.some(field => !field.ofOtherItem) ? edit : null
    }
}
